import React from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { FormHelperText, Grid, Divider, Stack, MenuItem, Button } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { Box } from '@mui/system';
import { useSnackbar } from 'notistack';
import TextField from '../../components/common/InputField/TextField';
import { styles } from './styles';
import {
    useCreateMasterCustomAttributeMutation,
    useUpdateMasterCustomAttributeMutation,
} from '../../api/masterAttributes';

const AddEditMasterAttributes = ({ id, data, handleClose, handleAction }) => {
    const { enqueueSnackbar } = useSnackbar();
    const [createMasterCustomAttribute, { isLoading: isCreating }] = useCreateMasterCustomAttributeMutation();
    const [updateMasterCustomAttribute, { isLoading: isUpdating }] = useUpdateMasterCustomAttributeMutation();

    const isLoading = isCreating || isUpdating;

    const handleCustomDetails = async (details) => {
        try {
            const bodyFormData = new FormData();
            for (const key in details) bodyFormData.append(key, details[key]);

            let response;
            if (id) {
                bodyFormData.append('id', id);
                response = await updateMasterCustomAttribute(bodyFormData).unwrap();
            } else {
                response = await createMasterCustomAttribute(bodyFormData).unwrap();
            }

            if (!response) return;
            if (response?.status === 'ERROR') throw new Error(response?.message);
            enqueueSnackbar(response?.message, { variant: 'success' });
            handleAction();
        } catch (error) {
            if (error?.statusCode === 400) enqueueSnackbar(error?.data?.message, { variant: 'error' });
            else enqueueSnackbar(error?.message || 'Something went wrong!', { variant: 'error' });
        }
    };

    const formik = useFormik({
        initialValues: {
            attributeName: data?.attributeName ?? '',
            status: data?.status ?? 'Active',
        },
        enableReinitialize: true,
        validationSchema: Yup.object().shape({
            attributeName: Yup.string().max(255).required('Attribute Name is required'),
            status: Yup.string().max(255).required('Status is required'),
        }),
        onSubmit: async (values, helpers) => {
            try {
                handleCustomDetails({ ...values, attributeName: values.attributeName?.trim().replace(/\s+/g, ' ') });
                //helpers.resetForm();
                helpers.setStatus({ success: true });
                helpers.setSubmitting(false);
            } catch (err) {
                console.error(err);
                helpers.setStatus({ success: false });
                helpers.setErrors({
                    submit: err.message,
                });
                helpers.setSubmitting(false);
            }
        },
    });

    return (
        <Box my={1}>
            <form onSubmit={formik.handleSubmit}>
                <div>
                    <Grid container columnSpacing={6} rowSpacing={3}>
                        <Grid item xs={4}>
                            <TextField
                                id="attributeName"
                                name="attributeName"
                                label="Attribute Name"
                                fullWidth
                                error={Boolean(formik.touched.attributeName && formik.errors.attributeName)}
                                helperText={formik.touched.attributeName && formik.errors.attributeName}
                                onBlur={formik.handleBlur}
                                onChange={formik.handleChange}
                                value={formik.values.attributeName}
                            />
                        </Grid>
                        <Grid item xs={4}>
                            <TextField
                                id="status"
                                name={'status'}
                                label="Status"
                                fullWidth
                                error={Boolean(formik.touched.status && formik.errors.status)}
                                helperText={formik.touched.status && formik.errors.status}
                                onBlur={formik.handleBlur}
                                onChange={formik.handleChange}
                                select
                                children={['Active', 'Inactive'].map((status) => (
                                    <MenuItem key={status} value={status}>
                                        {status}
                                    </MenuItem>
                                ))}
                                value={formik.values.status}
                            />
                        </Grid>
                        {formik.errors.submit && (
                            <Grid item xs={12}>
                                <FormHelperText error>{formik.errors.submit}</FormHelperText>
                            </Grid>
                        )}
                        <Grid item xs={12} sx={styles.dividerWrapper}>
                            <Divider flexItem orientation="horizontal" sx={styles.divider} />{' '}
                        </Grid>
                        <Grid item xs={12}>
                            <Stack direction="row" justifyContent="end">
                                <Button onClick={handleClose}>Cancel</Button>
                                <LoadingButton
                                    loading={isLoading}
                                    color="primary"
                                    size="large"
                                    type="submit"
                                    variant="contained"
                                    style={{ marginLeft: '16px' }}
                                >
                                    {id ? 'Update' : 'Create'}
                                </LoadingButton>
                            </Stack>
                        </Grid>
                    </Grid>
                </div>
            </form>
        </Box>
    );
};

export default AddEditMasterAttributes;
